// frontend/src/pages/TeacherLogs.tsx

import React, { useContext, useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { Teacher } from "../types/teacher";
import {
  SessionLog,
  CreateSessionLogRequest,
  UpdateSessionLogRequest,
} from "../types/sessionLog";
import { getTeacher } from "../api/teachers";
import {
  getSessionLogs,
  createSessionLog,
  updateSessionLog,
  deleteSessionLog,
} from "../api/sessionLogs";
import Sidebar from "../components/Sidebar";
import SessionLogForm from "../components/SessionLogForm";
import EditSessionLogForm from "../components/EditSessionLogForm";
import { Plus, Edit3, Trash2 } from "lucide-react";
// import "../styles/TeacherLogs.css";

export default function TeacherLogs() {
  const { id: teacherId } = useParams<{ id: string }>();
  const { user } = useContext(AuthContext);

  const [teacher, setTeacher] = useState<Teacher | null>(null);
  const [logs, setLogs] = useState<SessionLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [newCourseId, setNewCourseId] = useState<string | null>(null);
  const [editLog, setEditLog] = useState<SessionLog | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!teacherId) return;
      const [t, allLogs] = await Promise.all([
        getTeacher(teacherId),
        getSessionLogs({ teacher: teacherId }),
      ]);
      if (!mounted) return;
      setTeacher(t);
      setLogs(allLogs);
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [teacherId]);

  if (!user) return <Navigate to="/" replace />;
  if (loading) return <p className="loading-text">Loading…</p>;
  if (!teacher) return <p className="loading-text">Teacher not found</p>;

  // group logs by course
  type ByCourse = Record<
    string,
    { course: SessionLog["course"]; logs: SessionLog[] }
  >;
  const byCourse: ByCourse = {};
  logs.forEach((l) => {
    const cid = l.course.id;
    if (!byCourse[cid]) byCourse[cid] = { course: l.course, logs: [] };
    byCourse[cid].logs.push(l);
  });

  const refreshLogs = async () => {
    const fresh = await getSessionLogs({ teacher: teacherId! });
    setLogs(fresh);
  };
  const handleCreate = async (data: CreateSessionLogRequest) => {
    await createSessionLog(data);
    setNewCourseId(null);
    await refreshLogs();
  };
  const handleUpdate = async (id: string, data: UpdateSessionLogRequest) => {
    await updateSessionLog(id, data);
    setEditLog(null);
    await refreshLogs();
  };
  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this session?")) return;
    await deleteSessionLog(id);
    await refreshLogs();
  };

  return (
    <div className="teacher-logs-page">
      <Sidebar />
      <main className="teacher-logs-content">
        <header>
          <h1>{teacher.name}’s Session Logs</h1>
        </header>

        {teacher.courses.map((c) => (
          <section key={c.id} className="course-block">
            <div className="course-header">
              <h2>{c.name}</h2>
              <button onClick={() => setNewCourseId(c.id)}>
                <Plus /> Add Session
              </button>
            </div>

            <div className="table-container">
              <div className="overflow-x-auto">
                <table className="teachers-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Topic</th>
                      <th>Location</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byCourse[c.id]?.logs.map((l) => (
                      <tr key={l.id}>
                        <td>{new Date(l.date).toLocaleDateString()}</td>
                        <td>{l.startTime}</td>
                        <td>{l.endTime}</td>
                        <td>{l.topicCovered}</td>
                        <td>{l.location}</td>
                        <td>{l.notes}</td>
                        <td>
                          <button
                            className="action-btn action-btn-edit"
                            onClick={() => setEditLog(l)}
                            title="Edit Session"
                          >
                            <Edit3 />
                          </button>
                          <button
                            className="action-btn action-btn-delete"
                            onClick={() => handleDelete(l.id)}
                            title="Delete Session"
                          >
                            <Trash2 />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        ))}

        {newCourseId && (
          <SessionLogForm
            initial={{
              teacher: teacherId!,
              course: newCourseId,
              date: new Date().toISOString().substr(0, 10),
              startTime: "09:00",
              endTime: "10:00",
              topicCovered: "",
              notes: "",
            }}
            onSubmit={handleCreate}
            onCancel={() => setNewCourseId(null)}
          />
        )}

        {editLog && (
          <EditSessionLogForm
            log={editLog}
            onSubmit={handleUpdate}
            onCancel={() => setEditLog(null)}
          />
        )}
      </main>
    </div>
  );
}
